import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { translateMessage, translateMessageAsync } from '../utils/chatTranslation';
import ChatStatusIndicator from './ChatStatusIndicator';
import '../styles/ChatBox.css';
import logger from '../utils/logger';

const EMOJIS = ['😀', '😂', '😍', '🎉', '👍', '❤️', '🔥', '⚡', '🎯', '✨', '👑', '🦁', '🐉', '⚔️', '🛡️', '♟️'];

// Helper function to sanitize message display - hide image/binary data
const sanitizeMessageDisplay = (message) => {
  if (!message) return '';
  
  // Check for base64 image data patterns
  if (message.startsWith('data:image') || 
      message.startsWith('data:audio') ||
      message.startsWith('data:video') ||
      message.match(/^[A-Za-z0-9+/=]{50,}$/)) {
    return '📎 [Media attachment]';
  }
  
  // Check for very long strings of random characters (likely encoded data)
  if (message.length > 100 && !message.includes(' ') && /^[A-Za-z0-9+/=_-]+$/.test(message)) {
    return '📎 [Attachment]';
  }
  
  return message;
};

const ChatBox = ({ socketRef, gameId, roomId }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [showTranslations, setShowTranslations] = useState(true);
  const [asyncTranslations, setAsyncTranslations] = useState({});
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  const { language } = useTranslation();

  // Fetch AI translation for a message
  const fetchTranslation = useCallback(async (msgKey, text) => {
    if (!text || asyncTranslations[msgKey]) return;
    
    try {
      const result = await translateMessageAsync(text, language);
      if (result.translated) {
        setAsyncTranslations(prev => ({
          ...prev,
          [msgKey]: result.translated
        }));
      }
    } catch (err) {
      logger.error('Translation fetch error:', err);
    }
  }, [language, asyncTranslations]);

  useEffect(() => {
    // Access socket from ref inside effect (not during render)
    const socket = socketRef?.current;
    
    if (!socket) {
      setIsConnected(false);
      return;
    }

    // Check if socket is connected
    if (socket instanceof WebSocket) {
      setIsConnected(socket.readyState === WebSocket.OPEN);
    }

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'chat_message') {
          setMessages(prev => [...prev, {
            id: Date.now() + Math.random(),
            user: data.user_name || 'Player',
            userId: data.user_id,
            message: data.message,
            timestamp: new Date(data.timestamp || Date.now()),
            avatar: data.avatar || '👤',
            isSpectator: data.is_spectator
          }]);
        } else if (data.type === 'system_message') {
          setMessages(prev => [...prev, {
            id: Date.now() + Math.random(),
            user: 'System',
            userId: 'system',
            message: data.message,
            timestamp: new Date(),
            isSystem: true
          }]);
        } else if (data.type === 'player_joined') {
          setMessages(prev => [...prev, {
            id: Date.now() + Math.random(),
            user: 'System',
            userId: 'system',
            message: `A player joined the game`,
            timestamp: new Date(),
            isSystem: true
          }]);
        }
      } catch (error) {
        // Not all WebSocket messages are for chat - ignore parse errors
      }
    };

    const handleOpen = () => {
      setIsConnected(true);
    };
    
    const handleClose = () => {
      setIsConnected(false);
    };

    if (socket instanceof WebSocket) {
      socket.addEventListener('message', handleMessage);
      socket.addEventListener('open', handleOpen);
      socket.addEventListener('close', handleClose);
      
      // Check initial state
      if (socket.readyState === WebSocket.OPEN) {
        setIsConnected(true);
      }
      
      return () => {
        socket.removeEventListener('message', handleMessage);
        socket.removeEventListener('open', handleOpen);
        socket.removeEventListener('close', handleClose);
      };
    }
  }, [socketRef]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    const socket = socketRef?.current;
    
    if (!inputMessage.trim() || !socket) return;
    
    if (!(socket instanceof WebSocket) || socket.readyState !== WebSocket.OPEN) {
      logger.warn('💬 Cannot send message - WebSocket not connected');
      return;
    }

    const messageData = {
      type: 'chat_message',
      room_id: roomId || gameId,
      message: inputMessage.trim(),
      user_id: user?.user_id,
      user_name: user?.name || 'Player',
      avatar: user?.avatar || '👤'
    };
    socket.send(JSON.stringify(messageData));
    setInputMessage('');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const addEmoji = (emoji) => {
    setInputMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  return (
    <div className={`chat-box ${isMinimized ? 'minimized' : ''}`}>
      <div 
        className="chat-header"
        onClick={() => setIsMinimized(!isMinimized)}
        style={{ cursor: 'pointer' }}
        data-testid="chat-header"
      >
        <div className="chat-title">
          💬 Chat
          <ChatStatusIndicator
            status={isConnected ? 'connected' : 'connecting'}
            compact
          />
        </div>
        <button 
          className="minimize-btn"
          onClick={(e) => {
            e.stopPropagation(); // Prevent double toggle
            setIsMinimized(!isMinimized);
          }}
          data-testid="chat-minimize-btn"
        >
          {isMinimized ? '▲' : '▼'}
        </button>
      </div>

      {!isMinimized && (
        <>
          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="no-messages">
                <p>No messages yet</p>
                <p className="hint">Say hello to your opponent! 👋</p>
              </div>
            ) : (
              messages.map((msg) => {
                const msgKey = `chat-${msg.id}`;
                const isOwnMessage = msg.userId === user?.user_id;
                
                // First try phrase dictionary translation
                const phraseTranslation = !isOwnMessage && showTranslations && !msg.isSystem
                  ? translateMessage(msg.message, language)
                  : { original: msg.message, translated: null };
                
                // Check if we have an async AI translation
                const aiTranslation = asyncTranslations[msgKey];
                
                // Use phrase translation first, then AI translation
                const finalTranslation = phraseTranslation.translated || aiTranslation;
                
                // If phrase translation didn't work and we need AI translation, fetch it
                if (!isOwnMessage && showTranslations && !msg.isSystem && 
                    !phraseTranslation.translated && phraseTranslation.needsAiTranslation && !aiTranslation) {
                  fetchTranslation(msgKey, msg.message);
                }
                
                return (
                  <div 
                    key={msg.id} 
                    className={`chat-message ${msg.isSystem ? 'system' : ''} ${isOwnMessage ? 'own' : ''}`}
                  >
                    {!msg.isSystem && (
                      <>
                        <div className="message-avatar">{msg.avatar}</div>
                        <div className="message-content">
                          <div className="message-header">
                            <span className="message-user">
                              {msg.user}
                              {msg.isSpectator && <span className="spectator-badge">👀</span>}
                            </span>
                            <span className="message-time">{formatTime(msg.timestamp)}</span>
                          </div>
                          <div className="message-text">{sanitizeMessageDisplay(msg.message)}</div>
                          {finalTranslation && finalTranslation.toLowerCase() !== msg.message.toLowerCase() && (
                            <div className="message-translation">
                              <span className="translation-label">🌐</span>
                              <span className="translated-text">{finalTranslation}</span>
                            </div>
                          )}
                          {!finalTranslation && phraseTranslation.sourceLang && phraseTranslation.sourceLang !== language && (
                            <div className="message-translation loading">
                              <span className="translation-label">🌐</span>
                              <span className="translated-text lang-indicator">Translating...</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {msg.isSystem && (
                      <div className="system-message-text">{sanitizeMessageDisplay(msg.message)}</div>
                    )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-form" onSubmit={handleSendMessage}>
            {showEmojiPicker && (
              <div className="emoji-picker">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="emoji-button"
                    onClick={() => addEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
            
            <button
              type="button"
              className="emoji-trigger-btn"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              title="Add emoji"
            >
              😊
            </button>
            
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={isConnected ? "Type a message..." : "Connecting..."}
              maxLength={200}
              className="chat-input"
              disabled={!isConnected}
            />
            <button 
              type="submit" 
              className="send-btn"
              disabled={!inputMessage.trim() || !isConnected}
            >
              📤
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default ChatBox;
